import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { Check, ChevronsUpDown, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DatePicker } from '@/components/shared/DatePicker';
import type { AppShellContext } from '@/components/layout/AppShell';
import { useContractorsApi, useWarehouseApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { hasCompanyRole } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import {
  WZ_DELIVERY_METHODS,
  type ContractorListItem,
  type CreateWzDto,
  type WarehouseItem,
  type WzDetail,
  type WzDeliveryMethod,
} from '@ip/shared-types';

const DEFAULT_UNIT = 'szt.';

const PL_MAP: Record<string, string> = { ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z' };

function normalizeSearch(text: string): string {
  return text.toLowerCase().replace(/[ąćęłńóśźż]/g, (c) => PL_MAP[c] ?? c).replace(/[^a-z0-9]/g, '');
}

function contractorLabel(c: ContractorListItem): string {
  return c.nip ? `${c.name} (NIP ${c.nip})` : (c.name ?? '—');
}

function itemLabel(item: WarehouseItem): string {
  // color to hex — nieczytelny w etykiecie; kod + nazwa identyfikują pozycję.
  return `${item.code} — ${item.name}`;
}

interface WzLineForm {
  // UI-only: 'catalog' shows the item dropdown, 'manual' a free-text code input.
  // Never sent to the API — derived from itemId on load, stripped on submit.
  mode: 'catalog' | 'manual';
  itemId: string | null;
  code: string;
  description: string;
  unit: string;
  quantity: number;
}

interface WzFormValues {
  contractorId: string;
  issueDate: Date | null;
  deliveryMethod: WzDeliveryMethod | '';
  trackingNumber: string;
  notes: string;
  lines: WzLineForm[];
}

function emptyLine(): WzLineForm {
  return { mode: 'catalog', itemId: null, code: '', description: '', unit: DEFAULT_UNIT, quantity: 1 };
}

function toFormValues(wz: WzDetail | null): WzFormValues {
  if (!wz) {
    return {
      contractorId: '',
      issueDate: new Date(),
      deliveryMethod: '',
      trackingNumber: '',
      notes: '',
      lines: [emptyLine()],
    };
  }
  return {
    contractorId: wz.contractorId,
    issueDate: dayjs(wz.issueDate).toDate(),
    deliveryMethod: wz.deliveryMethod,
    trackingNumber: wz.trackingNumber ?? '',
    notes: wz.notes ?? '',
    lines: wz.lines.map((line) => ({
      mode: line.itemId ? 'catalog' : 'manual',
      itemId: line.itemId,
      code: line.code,
      description: line.description ?? '',
      unit: line.unit,
      quantity: line.quantity,
    })),
  };
}

/** Searchable buyer picker — returns a single contractorId (WZ never creates contractors). */
function ContractorSelect({
  contractors,
  value,
  onChange,
  disabled,
}: {
  contractors: ContractorListItem[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = useMemo(() => contractors.find((c) => c.id === value) ?? null, [contractors, value]);
  const filtered = useMemo(() => {
    if (!search) return contractors;
    const needle = normalizeSearch(search);
    return contractors.filter(
      (c) => normalizeSearch(c.name || '').includes(needle) || normalizeSearch(c.nip || '').includes(needle),
    );
  }, [contractors, search]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selected ? contractorLabel(selected) : t('magazyn.editor.buyerPlaceholder')}
          </span>
          <ChevronsUpDown className="size-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('magazyn.editor.buyerPlaceholder')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('contractors.noContractors')}</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  onSelect={() => {
                    onChange(c.id);
                    setSearch('');
                    setOpen(false);
                  }}
                >
                  <Check className={cn('size-4', value === c.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{contractorLabel(c)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Searchable catalog-item picker. Selecting prefills the line's Kod only (never the price). */
function ItemSelect({
  items,
  value,
  onSelect,
  disabled,
}: {
  items: WarehouseItem[];
  value: string | null;
  onSelect: (item: WarehouseItem) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = useMemo(() => items.find((i) => i.id === value) ?? null, [items, value]);
  const filtered = useMemo(() => {
    if (!search) return items;
    const needle = normalizeSearch(search);
    return items.filter(
      (i) =>
        normalizeSearch(i.code).includes(needle) ||
        normalizeSearch(i.name).includes(needle) ||
        normalizeSearch(i.color).includes(needle),
    );
  }, [items, search]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selected ? itemLabel(selected) : t('magazyn.editor.columns.item')}
          </span>
          <ChevronsUpDown className="size-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('magazyn.items.searchPlaceholder')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('magazyn.items.empty.title')}</CommandEmpty>
            <CommandGroup>
              {filtered.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => {
                    onSelect(item);
                    setSearch('');
                    setOpen(false);
                  }}
                >
                  <Check className={cn('size-4', value === item.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{itemLabel(item)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * WZ editor (react-hook-form, mirrors EmployeeForm): read-only "Wystawca" card
 * for the active company, searchable buyer + catalog-item pickers, a
 * useFieldArray line table, delivery method (courier reveals a required T&T
 * number), notes, and save/save+download actions. Prices never appear here —
 * the WZ surface carries none. A returned document is read-only for
 * non-admins; even for admins the buyer stays locked (mirrors the API).
 */
export function WzEditor() {
  const { nip, companyName } = useOutletContext<AppShellContext>();
  const { wzId } = useParams<{ wzId: string }>();
  const navigate = useNavigate();
  const { permissions, isSuperAdmin } = useAuth();
  const { getWz, createWz, updateWz, downloadWzPdf, listItems } = useWarehouseApi();
  const { getContractors } = useContractorsApi();

  const isEdit = Boolean(wzId);

  const [loading, setLoading] = useState(isEdit);
  const [forbidden, setForbidden] = useState(false);
  const [wz, setWz] = useState<WzDetail | null>(null);
  const [contractors, setContractors] = useState<ContractorListItem[]>([]);
  const [items, setItems] = useState<WarehouseItem[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<WzFormValues>({ defaultValues: toFormValues(null) });

  const { fields, append, remove } = useFieldArray({ control, name: 'lines' });

  useEffect(() => {
    let cancelled = false;
    getContractors(nip)
      .then((c) => { if (!cancelled) setContractors(c); })
      .catch(() => { if (!cancelled) toast.error(t('errors.loadingFailed')); });
    listItems(nip)
      .then((i) => { if (!cancelled) setItems(i); })
      .catch(() => { if (!cancelled) toast.error(t('errors.loadingFailed')); });
    return () => { cancelled = true; };
  }, [nip, getContractors, listItems]);

  useEffect(() => {
    if (!wzId) return;
    let cancelled = false;
    setLoading(true);
    getWz(nip, wzId)
      .then((detail) => {
        if (cancelled) return;
        setWz(detail);
        reset(toFormValues(detail));
        setForbidden(false);
      })
      .catch((error) => {
        if (cancelled) return;
        if ((error as { status?: number })?.status === 403) {
          setForbidden(true);
        } else {
          toast.error(t('magazyn.editor.saveFailed'));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [nip, wzId, getWz, reset]);

  const isReturned = wz?.status === 'returned';
  const canEditReturned = isSuperAdmin || hasCompanyRole(permissions, nip, ['admin']);
  // Returned documents already carry a signed scan — read-only for non-admins.
  const formDisabled = isReturned && !canEditReturned;
  // The buyer is frozen once a document is returned, even for admins (API rule).
  const buyerDisabled = formDisabled || isReturned;

  const deliveryMethod = watch('deliveryMethod');

  const submit = useCallback(
    async (values: WzFormValues, download: boolean) => {
      setSubmitting(true);
      // The save itself is the only step that can leave no record behind on
      // failure, so it gets its own try/catch — a PDF-download hiccup after a
      // successful save must never be reported as "failed to save" (the WZ
      // already exists), and must never leave the user on this form able to
      // resubmit "Zapisz i pobierz PDF" and mint a duplicate WZ / sequence
      // number. So we always navigate away once the save itself succeeded,
      // regardless of what happens to the PDF step.
      let saved: WzDetail;
      try {
        const dto: CreateWzDto = {
          issueDate: values.issueDate ? dayjs(values.issueDate).format('YYYY-MM-DD') : undefined,
          contractorId: values.contractorId,
          deliveryMethod: values.deliveryMethod as WzDeliveryMethod,
          trackingNumber: values.deliveryMethod === 'courier' ? values.trackingNumber.trim() : null,
          notes: values.notes.trim() || null,
          lines: values.lines.map((line) => ({
            itemId: line.mode === 'catalog' ? line.itemId : null,
            code: line.code.trim(),
            description: line.description.trim() || null,
            unit: line.unit.trim() || DEFAULT_UNIT,
            quantity: Number(line.quantity),
          })),
        };
        saved = wzId ? await updateWz(nip, wzId, dto) : await createWz(nip, dto);
      } catch {
        toast.error(t('magazyn.editor.saveFailed'));
        setSubmitting(false);
        return;
      }

      toast.success(t('magazyn.editor.savedToast', { number: saved.number }));

      if (download) {
        try {
          const blob = await downloadWzPdf(nip, saved.id);
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${saved.number.replace(/\//g, '_')}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch {
          toast.error(t('magazyn.editor.pdfDownloadFailed', { number: saved.number }));
        }
      }

      setSubmitting(false);
      navigate(`/${nip}/magazyn`);
    },
    [nip, wzId, createWz, updateWz, downloadWzPdf, navigate],
  );

  if (forbidden) {
    return <p className="text-muted-foreground py-10 text-center">{t('errors.accessDenied')}</p>;
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit((values) => submit(values, false))} className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          {isEdit
            ? t('magazyn.editor.titleEdit', { number: wz?.number ?? '' })
            : t('magazyn.editor.titleNew')}
        </h2>
      </div>

      {isReturned && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          {t('magazyn.editor.returnedBanner')}
        </div>
      )}

      <fieldset disabled={formDisabled} className="flex min-w-0 flex-col gap-6">
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="gap-1.5 p-4">
            <p className="text-muted-foreground text-xs">{t('magazyn.editor.seller')}</p>
            <p className="font-medium">{companyName}</p>
            <p className="text-muted-foreground text-sm">NIP {nip}</p>
          </Card>

          <div className="flex flex-col gap-4">
            <div className="space-y-1.5">
              <Label>{t('magazyn.editor.buyer')}</Label>
              <Controller
                name="contractorId"
                control={control}
                rules={{ required: t('magazyn.editor.buyerPlaceholder') }}
                render={({ field }) => (
                  <ContractorSelect
                    contractors={contractors}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={buyerDisabled}
                  />
                )}
              />
              {isReturned && (
                <p className="text-muted-foreground text-xs">{t('magazyn.editor.buyerLockedReturned')}</p>
              )}
              {errors.contractorId && (
                <p className="text-destructive text-xs">{errors.contractorId.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>{t('magazyn.editor.issueDate')}</Label>
              <Controller
                name="issueDate"
                control={control}
                render={({ field }) => (
                  <DatePicker value={field.value ?? null} onChange={field.onChange} disabled={formDisabled} />
                )}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold">{t('magazyn.editor.linesTitle')}</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={formDisabled}
              onClick={() => append(emptyLine())}
            >
              <Plus className="size-4" />
              {t('magazyn.editor.addLine')}
            </Button>
          </div>

          <div className="bg-card overflow-x-auto rounded-xl border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left">
                  <th className="w-12 px-3 py-2 font-medium">{t('magazyn.editor.ordinal')}</th>
                  <th className="min-w-[240px] px-3 py-2 font-medium">{t('magazyn.editor.columns.item')}</th>
                  <th className="min-w-[200px] px-3 py-2 font-medium">
                    {t('magazyn.editor.columns.description')}
                  </th>
                  <th className="w-24 px-3 py-2 font-medium">{t('magazyn.editor.columns.unit')}</th>
                  <th className="w-28 px-3 py-2 font-medium">{t('magazyn.editor.columns.quantity')}</th>
                  <th className="w-10 px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {fields.map((row, index) => {
                  const mode = watch(`lines.${index}.mode`);
                  return (
                    <tr key={row.id} className="border-b last:border-0 align-top">
                      <td className="px-3 py-2 tabular-nums">{index + 1}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-1.5">
                          <Controller
                            name={`lines.${index}.mode`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                value={field.value}
                                onValueChange={(v) => {
                                  field.onChange(v);
                                  if (v === 'manual') setValue(`lines.${index}.itemId`, null);
                                }}
                                disabled={formDisabled}
                              >
                                <SelectTrigger className="h-8 w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="catalog">{t('magazyn.editor.itemFromCatalog')}</SelectItem>
                                  <SelectItem value="manual">{t('magazyn.editor.itemManual')}</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {mode === 'catalog' ? (
                            <Controller
                              name={`lines.${index}.itemId`}
                              control={control}
                              rules={{
                                validate: (v) =>
                                  getValues(`lines.${index}.mode`) !== 'catalog' ||
                                  Boolean(v) ||
                                  t('magazyn.editor.codeRequired'),
                              }}
                              render={({ field }) => (
                                <ItemSelect
                                  items={items}
                                  value={field.value}
                                  onSelect={(item) => {
                                    field.onChange(item.id);
                                    setValue(`lines.${index}.code`, item.code);
                                    // Odbiorca WZ widzi tylko kod i opis — bez tego
                                    // prefillu nazwa towaru nie trafia na dokument.
                                    if (!getValues(`lines.${index}.description`)?.trim()) {
                                      setValue(`lines.${index}.description`, item.name);
                                    }
                                  }}
                                  disabled={formDisabled}
                                />
                              )}
                            />
                          ) : (
                            <Input
                              placeholder={t('magazyn.editor.columns.code')}
                              disabled={formDisabled}
                              {...register(`lines.${index}.code`, {
                                required: t('magazyn.editor.codeRequired'),
                              })}
                            />
                          )}
                          {(errors.lines?.[index]?.code || errors.lines?.[index]?.itemId) && (
                            <p className="text-destructive text-xs">
                              {errors.lines[index]?.code?.message ?? errors.lines[index]?.itemId?.message}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Input disabled={formDisabled} {...register(`lines.${index}.description`)} />
                      </td>
                      <td className="px-3 py-2">
                        <Input disabled={formDisabled} {...register(`lines.${index}.unit`)} />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          inputMode="decimal"
                          disabled={formDisabled}
                          {...register(`lines.${index}.quantity`, {
                            valueAsNumber: true,
                            validate: (v) => v > 0 || t('magazyn.editor.quantityInvalid'),
                          })}
                        />
                        {errors.lines?.[index]?.quantity && (
                          <p className="text-destructive text-xs">
                            {errors.lines[index]?.quantity?.message}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          aria-label={t('magazyn.editor.removeLine')}
                          disabled={formDisabled || fields.length === 1}
                          onClick={() => remove(index)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t('magazyn.editor.deliveryMethod')}</Label>
            <Controller
              name="deliveryMethod"
              control={control}
              rules={{ required: t('magazyn.editor.deliveryMethod') }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={formDisabled}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('magazyn.editor.deliveryMethod')} />
                  </SelectTrigger>
                  <SelectContent>
                    {WZ_DELIVERY_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {t(`magazyn.deliveryMethods.${m}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.deliveryMethod && (
              <p className="text-destructive text-xs">{errors.deliveryMethod.message}</p>
            )}
          </div>

          {deliveryMethod === 'courier' && (
            <div className="space-y-1.5">
              <Label htmlFor="wz-tracking">{t('magazyn.editor.trackingNumber')}</Label>
              <Input
                id="wz-tracking"
                disabled={formDisabled}
                {...register('trackingNumber', {
                  validate: (value) =>
                    getValues('deliveryMethod') !== 'courier' ||
                    value.trim().length > 0 ||
                    t('magazyn.editor.trackingNumberRequired'),
                })}
              />
              {errors.trackingNumber && (
                <p className="text-destructive text-xs">{errors.trackingNumber.message}</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="wz-notes">{t('magazyn.editor.notes')}</Label>
          <Textarea id="wz-notes" rows={3} disabled={formDisabled} {...register('notes')} />
        </div>
      </fieldset>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={formDisabled || submitting}>
          {submitting && <Loader2 className="size-4 animate-spin" />}
          {t('magazyn.editor.save')}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={formDisabled || submitting}
          onClick={handleSubmit((values) => submit(values, true))}
        >
          {t('magazyn.editor.saveAndDownload')}
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>
              <Button type="button" variant="outline" disabled title={t('magazyn.editor.fvSoon')}>
                {t('magazyn.editor.issueInvoice')}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{t('magazyn.editor.fvSoon')}</TooltipContent>
        </Tooltip>
      </div>
    </form>
  );
}
